import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ClientService } from '../../base/services/client/client.service';
import { ParkerRegistrationRawTableComponent } from './parker-registration-raw-table/parker-registration-raw-table.component';
import { ParkingSessionService } from './parking-session.service';
import { filter, map, startWith, switchMap, takeUntil, debounceTime, skip } from 'rxjs/operators';
import { DropDownConstantsFactory } from '../../base/shared/syncfusion/dropdown-constants-factory';
import {
  ClientDTO,
  FilterDTO,
  FilterOperator,
  ParkerRegistrationOffense,
  ParkerRegistrationType,
  SortDirection,
  ZoneClient,
  ZoneDTO,
} from '../../base/models/models.swagger.generated';
import { ParkerRegistrationDataType } from '../../base/models/parker-registration-data-type.enum';
import { FormBuilder, ReactiveFormsModule, FormControl } from '@angular/forms';
import { ParkerRegistrationService } from './parker-registration.service';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import { ParkerOffenseFilterType } from 'src/app/base/models/parker-offense-filter-type.enum';
import {
  ParkerRegistrationEvaluatedTableComponent,
} from './parker-registration-evaluated-table/parker-registration-evaluated-table.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { ButtonGroupComponent } from '../../base/shared/button-group/button-group.component';
import { AsyncPipe } from '@angular/common';
import { ClientAutocompleteComponent } from '../../base/shared/client-autocomplete/client-autocomplete.component';
import { TitleBarComponent } from '../../base/shared/title-bar/title-bar.component';
import { AuthService } from '../../base/services/auth.service';
import { LicensePlateAutocompleteComponent } from './license-plate-autocomplete/license-plate-autocomplete.component';
import { ParkerNameAutocompleteComponent } from './parker-name-autocomplete/parker-name-autocomplete.component';

type ParkerRegistrationFilterFormType = {
  dataType: FormControl<ParkerRegistrationDataType>;
  offenseType: FormControl<ParkerOffenseFilterType>;
  zone: FormControl<number>;
  present: FormControl<boolean>;
  parkerName: FormControl<string | null>;
  licensePlate: FormControl<string | null>;
};

@Component({
    selector: 'app-parker-registration',
    templateUrl: './parker-registration.component.html',
    styleUrls: ['./parker-registration.component.scss'],
    imports: [TitleBarComponent, ReactiveFormsModule, ClientAutocompleteComponent, ParkerNameAutocompleteComponent, LicensePlateAutocompleteComponent, ButtonGroupComponent, DropDownListModule, ParkerRegistrationRawTableComponent, ParkerRegistrationEvaluatedTableComponent, AsyncPipe, TranslateModule]
})
export class ParkerRegistrationComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('rawGrid') public rawGrid: ParkerRegistrationRawTableComponent;
  @ViewChild('evaluatedGrid') public evaluatedGrid: ParkerRegistrationEvaluatedTableComponent;

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient != value) {
      this.clientService.selectedClient = value;
      this.onClientSelected(value);
    }
  }

  public isAdmin = this.authService.isUserAdmin();

  public filterForm = this.fb.group<ParkerRegistrationFilterFormType>({
    dataType: this.fb.control(ParkerRegistrationDataType.Raw),
    offenseType: this.fb.control(null),
    zone: this.fb.control(null),
    present: this.fb.control(null),
    parkerName: this.fb.control<string | null>(null),
    licensePlate: this.fb.control<string | null>(null),
  });

  public get isAdminCrossClientSearch(): boolean {
    return this.isAdmin && !!(this.filterForm.controls.parkerName.value || this.filterForm.controls.licensePlate.value);
  }

  public statusList$ = this.translate.onLangChange
    .pipe(
      startWith(this.translate.currentLang),
      map(() => [
        {name: this.translate.instant('i18n.domain.parker-registration.filter.status.parker'), id: ParkerOffenseFilterType.Parker},
        {name: this.translate.instant('i18n.domain.parker-registration.filter.status.offense'), id: ParkerOffenseFilterType.Offense},
        {name: this.translate.instant('i18n.domain.parker-registration.filter.status.manual'), id: ParkerOffenseFilterType.ParkingRangerEntry}
      ])
    );
  public presentList$ = this.translate.onLangChange
    .pipe(
      startWith(this.translate.currentLang),
      map(() => [
        {name: this.translate.instant('i18n.domain.parker-registration.filter.is-present'), id: true},
        {name: this.translate.instant('i18n.domain.parker-registration.filter.is-not-present'), id: false},
      ])
    );

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();

  public ParkerRegistrationType = ParkerRegistrationType;
  public ParkerRegistrationOffense = ParkerRegistrationOffense;
  public ParkerRegistrationDataType = ParkerRegistrationDataType;

  public zones: ZoneDTO[] = [];

  public unsubscribe$ = new Subject<void>();
  public selectedClient$ = new BehaviorSubject<ClientDTO>(null);
  public zoneDropdownValues = [];

  constructor(private parkerRegistrationService: ParkerRegistrationService,
              private parkingService: ParkingSessionService,
              private clientService: ClientService,
              private zoneClient: ZoneClient,
              private translate: TranslateService,
              private fb: FormBuilder,
              private authService: AuthService,
              private route: ActivatedRoute) {
  }

  public ngOnInit(): void {
    const licensePlate = this.route.snapshot.queryParams['licensePlate'];
    if (licensePlate) {
      this.filterForm.controls.licensePlate.setValue(licensePlate, { emitEvent: false });
      this.syncFormStateToServices();
    }

    this.selectedClient$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(c => c && !!c.id),
        switchMap(c => {
          const groupFilter = new FilterDTO({
            isComplex: false,
            field: 'IsGroupingElement',
            operator: FilterOperator.Equal,
            value: 'false'
          });

          return this.zoneClient.searchZones(c.id, [groupFilter], 0, 0, 'Id', SortDirection.Ascending);
        }),
      ).subscribe(zones => {
        this.zones = zones.items;
        this.zoneDropdownValues = zones.items
          .filter(z => !z.isGroupingElement)
          .map(z => ({name: z.name, id: z.id}));
      });



    if (this.clientService.selectedClient) {
      this.selectedClient$.next(this.clientService.selectedClient);
      this.selectedClient = this.clientService.selectedClient;
    }
  }

  public ngAfterViewInit(): void {
    this.filterForm.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(400),
      )
      .subscribe(value => {
        this.parkerRegistrationService.offenseType = value.offenseType;
        this.parkingService.offenseType = value.offenseType;
        this.parkingService.present = value.present;
        this.parkingService.zone = value.zone;
        this.parkerRegistrationService.parkerName = value.parkerName ?? null;
        this.parkerRegistrationService.licensePlate = value.licensePlate ?? null;
        this.parkingService.parkerName = value.parkerName ?? null;
        this.parkingService.licensePlate = value.licensePlate ?? null;
        this.refreshGrid();
      });

    this.selectedClient$.pipe(
      takeUntil(this.unsubscribe$),
      skip(1),
      switchMap(client => client ? of(client) : timer(400).pipe(map(() => null as ClientDTO))),
    ).subscribe(client => {
      if (client || this.isAdminCrossClientSearch) {
        this.syncFormStateToServices();
        this.refreshGrid();
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.parkerRegistrationService.offenseType = null;
    this.parkingService.offenseType = null;
    this.parkingService.present = null;
    this.parkingService.zone = null;
    this.parkerRegistrationService.parkerName = null;
    this.parkerRegistrationService.licensePlate = null;
    this.parkingService.parkerName = null;
    this.parkingService.licensePlate = null;
  }

  public onParkerNameChange(value: string | null): void {
    this.filterForm.controls.parkerName.setValue(value ?? null);
  }

  public onLicensePlateChange(value: string | null): void {
    this.filterForm.controls.licensePlate.setValue(value ?? null);
  }

  public onClientSelected(clientDTO: ClientDTO): void {
    this.selectedClient = clientDTO;
    this.selectedClient$.next(clientDTO);
  }

  private syncFormStateToServices(): void {
    const value = this.filterForm.value;
    this.parkerRegistrationService.licensePlate = value.licensePlate ?? null;
    this.parkingService.licensePlate = value.licensePlate ?? null;
    this.parkerRegistrationService.parkerName = value.parkerName ?? null;
    this.parkingService.parkerName = value.parkerName ?? null;
  }

  private refreshGrid(): void {
    if (this.filterForm.controls.dataType.value === ParkerRegistrationDataType.Raw) {
      if (this.rawGrid) {
        this.rawGrid.refresh();
      }
    } else {
      if (this.evaluatedGrid) {
        this.evaluatedGrid.refresh();
      }
    }
  }
}
